<i18n
  src="@/locales/views/agro_exporter/providers/producers/new_crop_field.yml"
  lang="yaml"
></i18n>
<i18n src="@/locales/models/crop_field.yml" lang="yaml"></i18n>

<template>
  <go-back-link
    :text="$t('labels.go_back_link')"
    :name="routeName('show_producer')"
    :params="{
      agro_exporter_id: agro_exporter_id,
      producer_id: producer_id
    }"
  />
  <bread-crumbs
    :labels="[module_name, producer?.agro_exporter_producer?.producer_code]"
  ></bread-crumbs>

  <alert-danger :errors_data="std_err" />

  <template v-if="load_general_data">
    <producer-info :producer="producer" :agro_exporter_id="agro_exporter_id" />

    <div class="mt-8">
      <div class="panel">
        <div class="panel-header">
          <h3 class="panel-header-title">
            {{ $t("labels.title") }}
          </h3>
        </div>

        <div class="panel-body">
          <div class="space-y-8 divide-y divide-gray-200">
            <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.crop_field.name") }}
                </label>
                <div class="mt-1">
                  <input
                    type="text"
                    v-model.trim="name"
                    :class="['input-text', isInvalid('name')]"
                  />
                </div>
              </div>
            </div>
            <div class="pt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.crop_field.hectares") }}
                </label>
                <div class="mt-1">
                  <input
                    type="text"
                    v-model.trim="hectares"
                    :class="['input-text', isInvalid('hectares')]"
                  />
                </div>
              </div>
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.crop_field.tree_number") }}
                </label>
                <div class="mt-1">
                  <input
                    type="text"
                    v-model.trim="tree_number"
                    :class="['input-text', isInvalid('tree_number')]"
                  />
                </div>
              </div>
            </div>
            <div
              class="pt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6"
            >

              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.crop_field.crop_id") }}
                </label>
                <div class="mt-1">
                  <select-form
                    :options="crops"
                    v-model="crop_id"
                    label="name"
                    :is_invalid="isInvalidSelect('crop')"
                  />
                </div>
              </div>
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.crop_field.crop_variety_id") }}
                </label>
                <div class="mt-1">
                  <select-form
                    :options="crop_varieties"
                    v-model="crop_variety_id"
                    label="name"
                    :is_invalid="isInvalidSelect('crop_variety')"
                  />
                </div>
              </div>
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.crop_field.planting_date") }}
                </label>
                <div class="mt-1">
                  <date-form
                    v-model="planting_date"
                    mode="month"
                    :invalid-class="isInvalid('planting_date')"
                  />
                </div>
              </div>
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.crop_field.harvest_time") }}
                </label>
                <div class="mt-1">
                  <input
                    type="text"
                    v-model.trim="harvest_time"
                    :class="['input-text', isInvalid('harvest_time')]"
                  />
                </div>
              </div>
            </div>
            <div class="pt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.crop_field.location") }}
                </label>
                <div class="mt-1">
                  <input
                    type="text"
                    v-model="location"
                    :class="['input-text', isInvalid('location')]"
                  />
                </div>
              </div>
            </div>
            <div class="pt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.crop_field.longitude_dd") }}
                </label>
                <div class="mt-1">
                  <input
                    type="text"
                    v-model.trim="longitude_dd"
                    :class="['input-text', isInvalid('longitude_dd')]"
                  />
                  <em class="text-sm">Ejemplo: -80.701825</em>
                </div>
              </div>
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.crop_field.latitude_dd") }}
                </label>
                <div class="mt-1">
                  <input
                    type="text"
                    v-model.trim="latitude_dd"
                    :class="['input-text', isInvalid('latitude_dd')]"
                  />
                  <em class="text-sm">Ejemplo: -5.192999</em>
                </div>
              </div>
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.crop_field.altitude") }}
                </label>
                <div class="mt-1">
                  <input
                    type="text"
                    v-model.trim="altitude"
                    :class="['input-text', isInvalid('altitude')]"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="panel-footer">
          <div class="panel-footer-actions">
            <white-button
              @click.prevent="goToShowProducer()"
              :label="$t('labels.cancel_button')"
            />
            <span class="space-actions"></span>
            <success-button
              v-if="!proc_save_button"
              @click.prevent="create()"
              :label="$t('labels.save_button')"
            />
            <success-button-processing
              v-else
              :label="$t('labels.save_button')"
            />
          </div>
        </div>
      </div>
    </div>
  </template>

  <loading-message v-else />
</template>

<script>
import Requester from "@/views/agro_exporter/requesters/providers/producers/manage_crop_fields.js";
import RequesterManageProducer from "@/views/agro_exporter/requesters/providers/producers/manage_producers.js";
import CropFieldTypesMixin from "@/components/views/mixins/models/crop_field_types.js";
import ProducerInfo from "@/components/views/agro_exporter/providers/producers/ProducerInfo.vue";

export default {
  name: "AEProvidersProducersNewCropField",
  mixins: [CropFieldTypesMixin],
  components: { ProducerInfo },
  data() {
    return {
      load_general_data: false,
      proc_save_button: false,
      producer: {},
      crops: [],
      crop_varieties: [],
      name: null,
      hectares: null,
      square_meters: null,
      longitude_dd: null,
      latitude_dd: null,
      altitude: null,
      location: null,
      planting_date: null,
      harvest_time: null,
      tree_number: null,
      crop_id: null,
      crop_variety_id: null,
    };
  },
  props: {
    agro_exporter_id: {},
    producer_id: {}
  },
  methods: {
    loadData: async function () {
      await this.fetchProducer();
      await this.fetchCrops();
      this.load_general_data = true;
    },
    fetchProducer: async function () {
      let response = await RequesterManageProducer.fetchProducer(
        this.agro_exporter_id,
        this.producer_id
      );

      this.producer = response.data;
    },
    fetchCrops: async function () {
      let response = await Requester.fetchCrops(this.agro_exporter_id);

      this.crops = response.data;
      if (this.crops.length == 1) {
        this.crop_id = this.crops[0].id;
      }
    },
    create: async function () {
      this.proc_save_button = true;
      try {
        let response = await Requester.createCropField(
          this.agro_exporter_id,
          this.producer_id,
          {
            name: this.name,
            hectares: this.hectares,
            square_meters: this.square_meters,
            longitude_dd: this.longitude_dd,
            latitude_dd: this.latitude_dd,
            altitude: this.altitude,
            planting_date: this.planting_date,
            harvest_time: this.harvest_time,
            tree_number: this.tree_number,
            crop_id: this.crop_id,
            crop_variety_id: this.crop_variety_id,
            location: this.location
          }
        );

        this.registerSuccessNotification({
          title: response.data.success_message
        });
        this.goToShowProducer();
      } catch (error) {
        if (error.response.status == 422) {
          this.loadStandardErrorMessages({
            title: error.response.data.error_title,
            messages: error.response.data.error_messages,
            fields: error.response.data.error_fields
          });
        }
        this.proc_save_button = false;
      }
    },
    goToShowProducer: function () {
      this.$router.push({
        name: this.routeName("show_producer"),
        params: {
          agro_exporter_id: this.agro_exporter_id,
          producer_id: this.producer_id
        }
      });
    }
  },
  watch: {
    crop_id: function () {
      this.crop_variety_id = null;
      this.crop_varieties =
        this.crops.find(crop => crop.id == this.crop_id)?.crop_varieties || [];
      if (this.crop_varieties.length == 1) {
        this.crop_variety_id = this.crop_varieties[0].id;
      }
    }
  },
  created() {
    this.checkIfAuthenticated().then(() =>
      this.checkIfAuthorized({
        role: this.$route.meta.role,
        permission: this.$route.meta.permission,
        entity: {
          agro_exporter_id: this.agro_exporter_id
        }
      }).then(() => this.loadData())
    );
  }
};
</script>
